import { useState } from 'react';
import { useGmbReputationInsights } from '../../hooks/useGmbOptimization';
import { GBMContentWrapper } from '../governance/shared/GMBLoadingState';
import GBMStatCard from '../governance/shared/GBMStatCard';
import GMBEmptyState from '../governance/shared/GMBEmptyState';
import type { GmbReputationInsightsResponse } from '../../types';

interface Props {
  darkMode?: boolean;
  onNavigate?: (section: string) => void;
}

export default function ReviewReputationInsights({ darkMode, onNavigate }: Props) {
  const { data, isLoading, error, refetch } = useGmbReputationInsights();

  return (
    <GBMContentWrapper error={error} onRetry={() => refetch()} loading={isLoading}>
      <Inner darkMode={darkMode} onNavigate={onNavigate} data={(data || {}) as GmbReputationInsightsResponse} />
    </GBMContentWrapper>
  );
}

function Inner({ data }: Props & { data: GmbReputationInsightsResponse }) {
  const [hoveredMonth, setHoveredMonth] = useState<number | null>(null);
  const ratingData = data.rating_trend || [];
  const monthlySentiment = data.monthly_sentiment || [];
  const sentiment = data.sentiment || { positive_pct: 0, neutral_pct: 0, negative_pct: 0 };
  const chartWidth = 700;
  const chartHeight = 180;
  const padding = { top: 20, right: 30, bottom: 30, left: 30 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;
  const maxRating = 5;
  const minRating = 4;
  const getX = (index: number) => padding.left + (ratingData.length <= 1 ? 0 : (index / (ratingData.length - 1)) * innerWidth);
  const getY = (val: number) => padding.top + innerHeight - ((val - minRating) / (maxRating - minRating)) * innerHeight;
  const ratingPath = ratingData.map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(d.rating)}`).join(' ');

  if (!ratingData.length && !data.common_themes?.length && !data.response_pending_trend?.length) {
    return <GMBEmptyState title="No reputation data yet" message="Sync GMB reviews to unlock reputation insights." icon="ri-star-line" />;
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <GBMStatCard label="Current avg rating" value={(data.avg_rating || 0).toFixed(1)} change="Google reviews" color="orange" icon="ri-star-line" />
        <GBMStatCard label="Positive reviews" value={data.positive_reviews_count || 0} change={`${data.positive_reviews_pct || 0}% of total`} color="green" icon="ri-emotion-happy-line" />
        <GBMStatCard label="Responses pending" value={data.responses_pending || 0} change={`Oldest: ${data.oldest_pending_days || 0} days`} color="red" icon="ri-reply-line" />
        <GBMStatCard label="Reviews this year" value={data.reviews_this_year?.count || 0} change={`${data.reviews_this_year?.change_pct ?? 0}% vs previous`} color="blue" icon="ri-calendar-check-line" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">Rating Trend Movement</h3>
          <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full" style={{ minWidth: '300px' }}>
            {[4.0, 4.2, 4.4, 4.6, 4.8, 5.0].map((val) => (
              <g key={val}>
                <line x1={padding.left} y1={getY(val)} x2={chartWidth - padding.right} y2={getY(val)} stroke="#f3f4f6" />
                <text x={padding.left - 6} y={getY(val) + 4} textAnchor="end" fontSize="10" fill="#9ca3af">{val.toFixed(1)}</text>
              </g>
            ))}
            {ratingData.map((d, i) => (
              <text key={d.month} x={getX(i)} y={chartHeight - 8} textAnchor="middle" fontSize="11" fill="#6b7280">{d.month}</text>
            ))}
            <path d={ratingPath} fill="none" stroke="#0d9488" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            {ratingData.map((d, i) => (
              <g key={d.month} onMouseEnter={() => setHoveredMonth(i)} onMouseLeave={() => setHoveredMonth(null)}>
                <circle cx={getX(i)} cy={getY(d.rating)} r="5" fill="#0d9488" stroke="white" strokeWidth="2" />
                {hoveredMonth === i && (
                  <g>
                    <rect x={getX(i) - 45} y={getY(d.rating) - 38} width="90" height="30" rx="4" fill="#1f2937" />
                    <text x={getX(i)} y={getY(d.rating) - 20} textAnchor="middle" fontSize="10" fill="white">Rating: {d.rating}★</text>
                  </g>
                )}
              </g>
            ))}
          </svg>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">Review Sentiment Breakdown</h3>
          <div className="flex items-center gap-6 mb-4">
            <div className="relative w-28 h-28">
              <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f3f4f6" strokeWidth="3" />
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#059669" strokeWidth="3" strokeDasharray={`${sentiment.positive_pct} ${100 - sentiment.positive_pct}`} strokeLinecap="round" />
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f59e0b" strokeWidth="3" strokeDasharray={`${sentiment.neutral_pct} ${100 - sentiment.neutral_pct}`} strokeDashoffset={-sentiment.positive_pct} strokeLinecap="round" />
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#dc2626" strokeWidth="3" strokeDasharray={`${sentiment.negative_pct} ${100 - sentiment.negative_pct}`} strokeDashoffset={-(sentiment.positive_pct + sentiment.neutral_pct)} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{Math.round(sentiment.positive_pct)}%</span>
              </div>
            </div>
            <div className="space-y-2 flex-1">
              <Legend color="bg-green-600" label={`Positive (${Math.round(sentiment.positive_pct)}%)`} />
              <Legend color="bg-amber-500" label={`Neutral (${Math.round(sentiment.neutral_pct)}%)`} />
              <Legend color="bg-red-600" label={`Negative (${Math.round(sentiment.negative_pct)}%)`} />
            </div>
          </div>
          <div className="space-y-2">
            {monthlySentiment.map((d) => {
              const total = Math.max(d.positive + d.negative, 1);
              return (
                <div key={d.month} className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 w-10">{d.month}</span>
                  <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden flex">
                    <div className="h-full bg-green-500" style={{ width: `${(d.positive / total) * 100}%` }} />
                    <div className="h-full bg-red-400" style={{ width: `${(d.negative / total) * 100}%` }} />
                  </div>
                  <span className="text-[10px] text-gray-500 w-16 text-right">+{d.positive} / -{d.negative}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <DataTable
        title="Response Pending Trend"
        headers={['Reviewer', 'Rating', 'Days Pending', 'Theme', 'Urgency']}
        rows={(data.response_pending_trend || []).map((r) => [r.reviewer, `${r.rating}★`, `${r.days_pending} days`, r.theme, r.urgency])}
      />

      <div>
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">Common Review Themes & Keywords</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(data.common_themes || []).map((theme) => (
            <div key={theme.theme} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">{theme.theme}</h4>
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${theme.sentiment === 'positive' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                  {theme.sentiment}
                </span>
              </div>
              <div className="text-xs text-gray-500 mb-2">{theme.mention_count} mentions</div>
              <div className="flex flex-wrap gap-1.5">
                {theme.keywords.map((kw) => (
                  <span key={kw} className="px-2 py-0.5 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[10px]">{kw}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-start gap-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl p-4">
        <i className="ri-lightbulb-line text-teal-600 text-lg"></i>
        <div>
          <p className="text-sm font-medium text-teal-800 dark:text-teal-200">Operational Insight</p>
          <p className="text-xs text-teal-600 dark:text-teal-300 mt-0.5">{data.operational_insight || 'No operational insight available yet.'}</p>
        </div>
      </div>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return <div className="flex items-center gap-2"><span className={`w-3 h-3 rounded-full ${color}`} /><span className="text-xs text-gray-600 dark:text-gray-300">{label}</span></div>;
}

function DataTable({ title, headers, rows }: { title: string; headers: string[]; rows: string[][] }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr>{headers.map((h) => <th key={h} className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-500 uppercase">{h}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={headers.length} className="px-4 py-4 text-xs text-gray-500">No pending responses</td></tr> : rows.map((row, i) => (
              <tr key={i} className="border-t border-gray-50 dark:border-gray-700/50">{row.map((cell, j) => <td key={j} className="px-4 py-2.5 text-xs text-gray-700 dark:text-gray-300">{cell}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
